import type { Segment } from '@openx/types/gqlTypes';
import type { ConstValues } from '@openx/types/helpers';
import type { FieldPath, FieldPathValue, Path } from 'react-hook-form';
import type { AudienceShares } from '../types';
import type { ExportTypeValue, MatchingOperator } from './constants';

export type ExportTarget = 'cookie' | 'device' | 'idl' | 'ctv' | 'ip' | 'id5';

export const DemographicSegments = {
  AGE: 'demographic_age',
  GENDER: 'demographic_gender',
} as const;

// These types match the partial objects returned by GraphQL TreeSegment fragment
export type FormSegmentRegion = {
  __typename?: 'segment_region';
  region_code: string;
};

export type FormSegmentCountry = {
  __typename?: 'segment_country';
  country_code: string;
};

export type FormSegment = Pick<
  Segment,
  | 'id'
  | 'name'
  | 'description'
  | 'expiration_date'
  | 'status'
  | 'is_archived'
  | 'sub_category'
  | 'sub_type'
  | 'type'
  | 'parent_segment'
  | 'category'
  | 'is_leaf'
  | 'propensity'
  | 'reach'
  | 'price_cpm'
  | 'price_pom'
  | 'lookalike_definition_id'
  | 'segment_category'
  | 'segment_sub_category'
  | 'segment_type'
  | 'provider_id'
  | 'account_id'
  | 'sellable'
  | 'sharing_type'
  | 'is_nav_only'
> & {
  regions?: FormSegmentRegion[] | null;
  countries?: FormSegmentCountry[] | null;
};

export type LookalikeFormSegment = FormSegment & {
  similarity_level?: string;
  lookalike_definition_id?: string;
  sub_category?: LookalikeSpecificCategoryValues;
};

export const lookalikeSpecificCategory = {
  Similarity: 'similarity',
  SeedAggregate: 'seed_aggregate',
} as const;
export type LookalikeSpecificCategoryValues = ConstValues<typeof lookalikeSpecificCategory>;

type BaseGroup = {
  /**
   * id used for tracking expanded/collapsed state in the tree
   */
  uuid?: string;
  operator: MatchingOperator;
};

export type SegmentGroupItem<T> = { segments: T[]; operator: MatchingOperator };

export interface Group extends BaseGroup {
  demographic_age?: SegmentGroupItem<FormSegment>;
  demographic_gender?: SegmentGroupItem<FormSegment>;
  location?: SegmentGroupItem<FormSegment>;
  direct?: SegmentGroupItem<FormSegment>;
  predefined?: SegmentGroupItem<FormSegment>;
  lookalike?: SegmentGroupItem<LookalikeFormSegment>;
  region?: string | null;
  countries?: string[] | null;
}

export interface SubGroup extends BaseGroup {
  groups: GroupLike[];
}

export type GroupLike = Group | SubGroup;

export type TreeNode<T> = T & {
  children: TreeNode<T>[];
};

export type SegmentTreeNode = TreeNode<FormSegment>;

export type GroupsContainer = {
  operator: MatchingOperator;
  groups: GroupLike[];
};

export type AudienceFormBase = {
  account_id: string;
  export_type: ExportTypeValue | null;
  name: string;
  description: string;
  export_targets: Record<string, boolean>;
  direct_audience_provider: string | null;
  normalized_definition: {
    excludes: GroupsContainer;
    includes: GroupsContainer;
  };
};

export type AudienceShareItem = {
  recipient_account_id: string[];
  audience_external_openaudience_id: string;
  isFeeEnabled: boolean;
  revenue_method: 'CPM' | 'PoM';
  value: string;
};

export type AudienceShareRecipient = AudienceShareItem;
export type AudienceForm = AudienceFormBase & AudienceShares;

export type AudienceFormGetValuesForPath = <TFieldName extends FieldPath<AudienceForm>>(
  path: TFieldName
) => FieldPathValue<AudienceForm, TFieldName> | undefined;

export const ListType = {
  INCLUDES: 'includes',
  EXCLUDES: 'excludes',
} as const;
export type AudienceListType = ConstValues<typeof ListType>;

export type GroupFieldName =
  | `normalized_definition.${AudienceListType}.groups`
  | `normalized_definition.${AudienceListType}.groups.${number}`
  | `normalized_definition.${AudienceListType}.groups.${number}.groups.${number}`;

export type IndexedGroupFieldName =
  | `normalized_definition.${AudienceListType}.groups.${number}`
  | (`normalized_definition.${AudienceListType}.groups.${number}.groups.${number}` & Path<AudienceForm>);

type Propensity = 'all' | 'low' | 'medium' | 'high';

export type DataTypeFilters = {
  past_browsing_interests?: 'all';
  lookalike?: 'all';
  lifestyles?: Propensity;
  attitudes?: 'all';
  location_visits?: 'all';
  shopping?: Propensity;
  advanced_demographics?: Propensity;
  purchase_propensity?: Propensity;
  marketplace?: 'all';
};

export type DataTypeFilterKeys = keyof DataTypeFilters;

export type GroupsPath = 'normalized_definition.includes.groups' | 'normalized_definition.excludes.groups';

export type GroupIndexFieldName = `${GroupsPath}.${number}` & Path<AudienceForm>;

export const IncludesGroupName = 'normalized_definition.includes' as const;
export const ExcludesGroupName = 'normalized_definition.excludes' as const;

export const AudienceFields = {
  ExportType: 'export_type',
  DirectAudienceProvider: 'direct_audience_provider',
  OrganizationId: 'account_id',
  NormalizedDefinition: 'normalized_definition',
  ExportTargets: 'export_targets',
} as const;

export const similarityLevels = {
  High: 'High',
  Balanced: 'Balanced',
  Scaled: 'Scaled',
} as const;
export type SimilarityLevelValue = ConstValues<typeof similarityLevels>;

export type LookalikeSegmentDefinition = {
  id: string;
  account_id: string;
  provider_id: string | null;
  lookalike_name: string;
  status: string;
  created_date?: string;
  updated_date?: string;
  lookalike_segment_seed_aggregate?: {
    segment: FormSegment;
  };
  lookalike_segment_similarities: Array<{
    similarity_level: SimilarityLevelValue;
    segment: FormSegment;
  }>;
};
